import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, Edit, Trash2, X, ArrowRightLeft, Wallet, TrendingUp, TrendingDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { accountsAPI, accountTransactionsAPI, settingsAPI } from '../lib/api';

const AccountTracking = () => {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({
    USD: 1,
    EUR: 1,
    GBP: 1,
    GOLD: 1
  });

  // Form states
  const [accountForm, setAccountForm] = useState({
    name: '',
    currency: 'TRY',
    balance: 0,
    is_active: true
  });

  const [transactionForm, setTransactionForm] = useState({
    account_id: '',
    amount: '',
    currency: 'TRY',
    transaction_type: 'income',
    transaction_date: new Date().toISOString().split('T')[0],
    description: ''
  });

  const [convertForm, setConvertForm] = useState({
    from_account_id: '',
    to_account_id: '',
    amount: '', // Kaynak hesap biriminden tutar
    to_amount: '', // Hedef hesap biriminden tutar
    exchange_rate: ''
  });

  // Analiz filtreleri
  const [analysisFilters, setAnalysisFilters] = useState({
    account_id: 'all', // 'all' veya hesap ID'si
    transaction_type: 'all', // 'all', 'income', 'expense'
    period: 'monthly', // 'monthly', 'yearly'
    start_date: '',
    end_date: ''
  });

  // Sayfalandırma state'leri
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [analysisPage, setAnalysisPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
    fetchExchangeRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sayfa değiştiğinde sayfa numaralarını sıfırla
  useEffect(() => {
    setTransactionsPage(1);
  }, [transactions]);

  useEffect(() => {
    setAnalysisPage(1);
  }, [analysisFilters]);

  const fetchExchangeRates = async () => {
    try {
      const settings = await settingsAPI.getAll();
      setExchangeRates({
        USD: parseFloat(settings.exchange_rate_usd) || 32,
        EUR: parseFloat(settings.exchange_rate_eur) || 35,
        GBP: parseFloat(settings.exchange_rate_gbp) || 40,
        GOLD: parseFloat(settings.exchange_rate_gold) || 2500
      });
    } catch (error) {
      console.error(t('accountTracking.exchangeRateError', 'Kur bilgileri alınamadı'), error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [accountsData, transactionsData] = await Promise.all([
        accountsAPI.getAll(),
        accountTransactionsAPI.getAll()
      ]);
      
      setAccounts(Array.isArray(accountsData) ? accountsData : []);
      setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
    } catch (error) {
      console.error(t('accountTracking.fetchError', 'Veri getirme hatası'), error);
      toast.error(t('accountTracking.fetchErrorMsg', 'Veriler alınamadı') + ': ' + (error.message || t('common.unknownError', 'Bilinmeyen hata')));
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'TRY': '₺',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'GOLD': 'Altın'
    };
    return symbols[currency] || currency;
  };

  const handleCreateAccount = async () => {
    try {
      if (!accountForm.name || !accountForm.currency) {
        toast.error('Lütfen hesap adı ve para birimi girin');
        return;
      }

      await accountsAPI.create(accountForm);
      toast.success(t('accountTracking.accountCreated', 'Hesap başarıyla oluşturuldu'));
      setShowAccountModal(false);
      resetAccountForm();
      fetchData();
    } catch (error) {
      console.error(t('accountTracking.createAccountError', 'Hesap oluşturma hatası'), error);
      toast.error(t('accountTracking.createAccountErrorMsg', 'Hesap oluşturulamadı') + ': ' + (error.message || t('common.unknownError', 'Bilinmeyen hata')));
    }
  };

  const handleUpdateAccount = async () => {
    try {
      if (!accountForm.name || !accountForm.currency) {
        toast.error('Lütfen hesap adı ve para birimi girin');
        return;
      }

      await accountsAPI.update(editingAccount.id, accountForm);
      toast.success(t('accountTracking.accountUpdated', 'Hesap başarıyla güncellendi'));
      setShowAccountModal(false);
      setEditingAccount(null);
      resetAccountForm();
      fetchData();
    } catch (error) {
      console.error(t('accountTracking.updateAccountError', 'Hesap güncelleme hatası'), error);
      toast.error(t('accountTracking.updateAccountErrorMsg', 'Hesap güncellenemedi') + ': ' + (error.message || t('common.unknownError', 'Bilinmeyen hata')));
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm(t('accountTracking.confirmDeleteAccount', 'Bu hesabı silmek istediğinizden emin misiniz? Tüm işlemler de silinecektir.'))) {
      return;
    }

    try {
      await accountsAPI.delete(id);
      toast.success(t('accountTracking.accountDeleted', 'Hesap başarıyla silindi'));
      fetchData();
    } catch (error) {
      console.error(t('accountTracking.deleteAccountError', 'Hesap silme hatası'), error);
      toast.error(t('accountTracking.deleteAccountErrorMsg', 'Hesap silinemedi') + ': ' + (error.message || t('common.unknownError', 'Bilinmeyen hata')));
    }
  };

  const handleCreateTransaction = async () => {
    try {
      if (!transactionForm.account_id || !transactionForm.amount || !transactionForm.transaction_date) {
        toast.error('Lütfen tüm alanları doldurun');
        return;
      }

      await accountTransactionsAPI.create(transactionForm);
      toast.success(t('accountTracking.transactionCreated', 'İşlem başarıyla eklendi'));
      setShowTransactionModal(false);
      resetTransactionForm();
      fetchData();
    } catch (error) {
      console.error(t('accountTracking.createTransactionError', 'İşlem ekleme hatası'), error);
      toast.error(t('accountTracking.createTransactionErrorMsg', 'İşlem eklenemedi') + ': ' + (error.message || t('common.unknownError', 'Bilinmeyen hata')));
    }
  };

  const handleUpdateTransaction = async () => {
    try {
      if (!transactionForm.account_id || !transactionForm.amount || !transactionForm.transaction_date) {
        toast.error('Lütfen tüm alanları doldurun');
        return;
      }

      await accountTransactionsAPI.update(editingTransaction.id, transactionForm);
      toast.success(t('accountTracking.transactionUpdated', 'İşlem başarıyla güncellendi'));
      setShowTransactionModal(false);
      setEditingTransaction(null);
      resetTransactionForm();
      fetchData();
    } catch (error) {
      console.error(t('accountTracking.updateTransactionError', 'İşlem güncelleme hatası'), error);
      toast.error(t('accountTracking.updateTransactionErrorMsg', 'İşlem güncellenemedi') + ': ' + (error.message || t('common.unknownError', 'Bilinmeyen hata')));
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm(t('accountTracking.confirmDeleteTransaction', 'Bu işlemi silmek istediğinizden emin misiniz?'))) {
      return;
    }

    try {
      await accountTransactionsAPI.delete(id);
      toast.success(t('accountTracking.transactionDeleted', 'İşlem başarıyla silindi'));
      fetchData();
    } catch (error) {
      console.error(t('accountTracking.deleteTransactionError', 'İşlem silme hatası'), error);
      toast.error(t('accountTracking.deleteTransactionErrorMsg', 'İşlem silinemedi') + ': ' + (error.message || t('common.unknownError', 'Bilinmeyen hata')));
    }
  };

  const handleConvertCurrency = async () => {
    try {
      // Kaynak veya hedef tutardan birini kontrol et
      const fromAmount = parseFloat(convertForm.amount) || 0;
      const toAmount = parseFloat(convertForm.to_amount) || 0;
      
      if (!convertForm.from_account_id || !convertForm.to_account_id) {
        toast.error(t('accountTracking.selectBothAccounts', 'Lütfen kaynak ve hedef hesapları seçin'));
        return;
      }

      if (!convertForm.exchange_rate || parseFloat(convertForm.exchange_rate) <= 0) {
        toast.error(t('accountTracking.enterValidRate', 'Lütfen geçerli bir çevirme oranı girin'));
        return;
      }

      if (!fromAmount && !toAmount) {
        toast.error(t('accountTracking.enterAmount', 'Lütfen kaynak veya hedef tutardan birini girin'));
        return;
      }

      // Eğer hedef tutar girildiyse, kaynak tutarı hesapla
      let finalFromAmount = fromAmount;
      if (toAmount > 0 && fromAmount === 0) {
        const rate = parseFloat(convertForm.exchange_rate) || 1;
        finalFromAmount = toAmount / rate;
      }

      // UUID string karşılaştırması (parseInt kullanma!)
      const fromAccount = accounts.find(a => String(a.id) === String(convertForm.from_account_id));
      if (!fromAccount) {
        toast.error(t('accountTracking.sourceAccountNotFound', 'Kaynak hesap bulunamadı'));
        return;
      }
      
      if (parseFloat(fromAccount.balance) < finalFromAmount) {
        toast.error(t('accountTracking.insufficientBalance', 'Yetersiz bakiye'));
        return;
      }

      await accountTransactionsAPI.convertCurrency(
        convertForm.from_account_id,
        convertForm.to_account_id,
        finalFromAmount,
        parseFloat(convertForm.exchange_rate)
      );
      
      toast.success(t('accountTracking.currencyConverted', 'Para birimi başarıyla çevrildi'));
      setShowConvertModal(false);
      resetConvertForm();
      fetchData();
    } catch (error) {
      toast.error(t('accountTracking.convertErrorMsg', 'Para birimi çevrilemedi') + ': ' + (error.message || t('common.unknownError', 'Bilinmeyen hata')));
    }
  };

  const resetAccountForm = () => {
    setAccountForm({
      name: '',
      currency: 'TRY',
      balance: 0,
      is_active: true
    });
  };

  const resetTransactionForm = () => {
    setTransactionForm({
      account_id: '',
      amount: '',
      currency: 'TRY',
      transaction_type: 'income',
      transaction_date: new Date().toISOString().split('T')[0],
      description: ''
    });
  };

  const resetConvertForm = () => {
    setConvertForm({
      from_account_id: '',
      to_account_id: '',
      amount: '',
      to_amount: '',
      exchange_rate: ''
    });
  };

  const openEditAccount = (account) => {
    setEditingAccount(account);
    setAccountForm({
      name: account.name,
      currency: account.currency,
      balance: account.balance,
      is_active: account.is_active
    });
    setShowAccountModal(true);
  };

  const openEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setTransactionForm({
      account_id: transaction.account_id,
      amount: transaction.amount,
      currency: transaction.currency,
      transaction_type: transaction.transaction_type,
      transaction_date: transaction.transaction_date,
      description: transaction.description || ''
    });
    setShowTransactionModal(true);
  };

  const openConvertModal = () => {
    if (accounts.filter(a => a.is_active).length < 2) {
      toast.error(t('accountTracking.needTwoAccounts', 'Para birimi çevirmek için en az 2 aktif hesap gerekli'));
      return;
    }
    setShowConvertModal(true);
  };

  // Para birimi çevirme oranını otomatik hesapla (sadece kullanıcı manuel girmemişse)
  useEffect(() => {
    if (convertForm.from_account_id && convertForm.to_account_id) {
      // UUID string karşılaştırması (parseInt kullanma!)
      const fromAccount = accounts.find(a => String(a.id) === String(convertForm.from_account_id));
      const toAccount = accounts.find(a => String(a.id) === String(convertForm.to_account_id));
      
      if (fromAccount && toAccount && fromAccount.currency !== toAccount.currency) {
        let rate = 1;
        
        // Altın özel durumu: GOLD fiyatı gram başına TL cinsinden
        // Örnek: exchangeRates['GOLD'] = 5900 (1 gram altın = 5900 TL)
        
        // Adım 1: Kaynak para birimini TL'ye çevir
        let fromInTRY = 1;
        if (fromAccount.currency === 'GOLD') {
          // 1 gram altın = GOLD_rate TL
          fromInTRY = exchangeRates['GOLD'] || 1;
        } else if (fromAccount.currency === 'TRY') {
          // 1 TL = 1 TL
          fromInTRY = 1;
        } else {
          // 1 USD/EUR/GBP = rate TL
          fromInTRY = exchangeRates[fromAccount.currency] || 1;
        }
        
        // Adım 2: TL'yi hedef para birimine çevir
        if (toAccount.currency === 'GOLD') {
          // TL'den altına: 1 TL = 1 / GOLD_rate gram altın
          // Yani: fromInTRY TL = fromInTRY / GOLD_rate gram altın
          const goldRate = exchangeRates['GOLD'];
          if (goldRate && goldRate > 0) {
            rate = fromInTRY / goldRate;
          } else {
            // GOLD rate yoksa veya 0 ise, 1 kullanma, kullanıcıya uyarı ver
            rate = 0;
                toast.error(t('accountTracking.goldRateNotFound', 'Altın kuru bulunamadı. Lütfen ayarlardan altın kurunu güncelleyin.'));
          }
        } else if (toAccount.currency === 'TRY') {
          // TL'ye çevirme: rate = fromInTRY
          rate = fromInTRY;
        } else {
          // TL'den USD/EUR/GBP'ye: 1 TL = 1 / rate USD/EUR/GBP
          // Yani: fromInTRY TL = fromInTRY / rate USD/EUR/GBP
          const targetRate = exchangeRates[toAccount.currency];
          if (targetRate && targetRate > 0) {
            rate = fromInTRY / targetRate;
          } else {
            rate = 0;
                toast.error(t('accountTracking.currencyRateNotFound', '{currency} kuru bulunamadı. Lütfen ayarlardan kurları güncelleyin.').replace('{currency}', toAccount.currency));
          }
        }
        
        // Çevirme oranı için maksimum 12 ondalık hane
        const ratePrecision = 12;
        
        // Sadece exchange_rate boşsa veya hesap değiştiyse güncelle (kullanıcı manuel girmişse değiştirme)
        // Eğer rate 0 ise, güncelleme yapma (kullanıcı manuel girebilir)
        if (rate > 0) {
          setConvertForm(prev => {
            // Eğer kullanıcı manuel olarak kur girmişse, otomatik hesaplamayı atla
            // Şimdilik sadece boşsa veya hesap değiştiyse güncelle
            if (!prev.exchange_rate || prev.exchange_rate === '1.0000' || prev.exchange_rate === '1.000000' || prev.exchange_rate === '1.000000000000') {
              return { ...prev, exchange_rate: rate.toFixed(ratePrecision) };
            }
            return prev;
          });
        }
      } else {
        setConvertForm(prev => ({ ...prev, exchange_rate: '1.0000' }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertForm.from_account_id, convertForm.to_account_id, exchangeRates, accounts]);

  // Kaynak tutar değiştiğinde hedef tutarı hesapla (sadece hedef tutar boşsa)
  useEffect(() => {
    if (convertForm.from_account_id && convertForm.to_account_id && convertForm.amount && convertForm.exchange_rate) {
      const fromAmount = parseFloat(convertForm.amount) || 0;
      const toAmount = parseFloat(convertForm.to_amount) || 0;
      
      // Sadece kaynak tutar dolu ve hedef tutar boşsa hesapla
      if (fromAmount > 0 && toAmount === 0) {
        const rate = parseFloat(convertForm.exchange_rate) || 1;
        const calculatedToAmount = fromAmount * rate;
        // UUID string karşılaştırması (parseInt kullanma!)
        const toAccount = accounts.find(a => String(a.id) === String(convertForm.to_account_id));
        const formatOptions = toAccount?.currency === 'GOLD' 
          ? { minimumFractionDigits: 6, maximumFractionDigits: 6 }
          : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
        setConvertForm(prev => ({ 
          ...prev, 
          to_amount: calculatedToAmount.toFixed(formatOptions.maximumFractionDigits)
        }));
      }
    }
  }, [convertForm.amount, convertForm.exchange_rate, convertForm.from_account_id, convertForm.to_account_id, convertForm.to_amount, accounts]);

  // Hedef tutar değiştiğinde kaynak tutarı hesapla (sadece kaynak tutar boşsa)
  useEffect(() => {
    if (convertForm.from_account_id && convertForm.to_account_id && convertForm.to_amount) {
      const fromAmount = parseFloat(convertForm.amount) || 0;
      const toAmount = parseFloat(convertForm.to_amount) || 0;
      
      // Sadece hedef tutar dolu ve kaynak tutar boşsa hesapla
      if (toAmount > 0 && fromAmount === 0) {
        // UUID string karşılaştırması (parseInt kullanma!)
        const fromAccount = accounts.find(a => String(a.id) === String(convertForm.from_account_id));
        const toAccount = accounts.find(a => String(a.id) === String(convertForm.to_account_id));
        
        if (fromAccount && toAccount) {
          let rate = parseFloat(convertForm.exchange_rate) || 1;
          
          // Eğer kaynak hesap TL ise ve hedef hesap başka bir para birimi ise, sitedeki kurları kullan
          if (fromAccount.currency === 'TRY' && toAccount.currency !== 'TRY') {
            // TL'den hedef para birimine çevirme oranını hesapla
            if (toAccount.currency === 'GOLD') {
              // TL'den altına: 1 TL = 1 / GOLD_rate gram altın
              const goldRate = exchangeRates['GOLD'];
              if (goldRate && goldRate > 0) {
                rate = 1 / goldRate;
              } else {
                rate = 0;
                toast.error(t('accountTracking.goldRateNotFound', 'Altın kuru bulunamadı. Lütfen ayarlardan altın kurunu güncelleyin.'));
              }
            } else {
              // TL'den USD/EUR/GBP'ye: 1 TL = 1 / rate USD/EUR/GBP
              const targetRate = exchangeRates[toAccount.currency];
              if (targetRate && targetRate > 0) {
                rate = 1 / targetRate;
              } else {
                rate = 0;
                toast.error(t('accountTracking.currencyRateNotFound', '{currency} kuru bulunamadı. Lütfen ayarlardan kurları güncelleyin.').replace('{currency}', toAccount.currency));
              }
            }
            
            // Kur'u güncelle (sadece rate > 0 ise)
            if (rate > 0) {
              const ratePrecision = 12; // Maksimum 12 ondalık hane
              setConvertForm(prev => ({ 
                ...prev, 
                exchange_rate: rate.toFixed(ratePrecision)
              }));
            }
          }
          
          if (rate > 0) {
            const calculatedFromAmount = toAmount / rate;
            const formatOptions = fromAccount?.currency === 'GOLD' 
              ? { minimumFractionDigits: 6, maximumFractionDigits: 6 }
              : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
            setConvertForm(prev => ({ 
              ...prev, 
              amount: calculatedFromAmount.toFixed(formatOptions.maximumFractionDigits)
            }));
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertForm.to_amount, convertForm.exchange_rate, convertForm.from_account_id, convertForm.to_account_id, convertForm.amount, accounts, exchangeRates]);

  const getAccountTransactions = (accountId) => {
    // UUID karşılaştırması için string'e çevir
    const accountIdStr = String(accountId);
    return transactions.filter(t => String(t.account_id) === accountIdStr);
  };

  // Filtrelenmiş analiz
  const getFilteredAnalysis = () => {
    // Filtrelenmiş transaction'ları al
    let filteredTransactions = [...transactions];

    // Hesap filtresi
    if (analysisFilters.account_id !== 'all') {
      // UUID karşılaştırması için string'e çevir
      const filterAccountId = String(analysisFilters.account_id);
      filteredTransactions = filteredTransactions.filter(
        t => String(t.account_id) === filterAccountId
      );
    }

    // Transaction type filtresi
    if (analysisFilters.transaction_type !== 'all') {
      filteredTransactions = filteredTransactions.filter(
        t => t.transaction_type === analysisFilters.transaction_type
      );
    }

    // Tarih filtresi
    if (analysisFilters.start_date) {
      filteredTransactions = filteredTransactions.filter(
        t => new Date(t.transaction_date) >= new Date(analysisFilters.start_date)
      );
    }
    if (analysisFilters.end_date) {
      filteredTransactions = filteredTransactions.filter(
        t => new Date(t.transaction_date) <= new Date(analysisFilters.end_date)
      );
    }

    // Gruplama - Para birimi ve tarih bazında
    const grouped = {};
    let totalIncome = 0;
    let totalExpense = 0;

    filteredTransactions.forEach(transaction => {
      const date = new Date(transaction.transaction_date);
      let periodKey = '';
      let periodName = '';
      const currency = transaction.currency || 'TRY';

      if (analysisFilters.period === 'monthly') {
        periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        periodName = date.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });
      } else {
        periodKey = String(date.getFullYear());
        periodName = date.getFullYear().toString();
      }

      // Para birimi ve dönem kombinasyonu için key
      const groupKey = `${periodKey}_${currency}`;

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          key: groupKey,
          periodKey,
          periodName,
          currency,
          income: 0,
          expense: 0,
          profit: 0,
          transactions: []
        };
      }

      const amount = parseFloat(transaction.amount || 0);
      if (transaction.transaction_type === 'income') {
        grouped[groupKey].income += amount;
        totalIncome += amount;
      } else {
        grouped[groupKey].expense += amount;
        totalExpense += amount;
      }

      grouped[groupKey].transactions.push(transaction);
    });

    // Kar hesapla
    Object.keys(grouped).forEach(key => {
      grouped[key].profit = grouped[key].income - grouped[key].expense;
    });

    // Dönem ve para birimine göre sırala
    const sortedGroups = Object.values(grouped).sort((a, b) => {
      // Önce dönem, sonra para birimi
      if (a.periodKey !== b.periodKey) {
        return b.periodKey.localeCompare(a.periodKey);
      }
      return a.currency.localeCompare(b.currency);
    });

    // Para birimlerine göre toplam hesapla
    const totalsByCurrency = {};
    filteredTransactions.forEach(transaction => {
      const currency = transaction.currency || 'TRY';
      if (!totalsByCurrency[currency]) {
        totalsByCurrency[currency] = {
          currency,
          income: 0,
          expense: 0,
          profit: 0
        };
      }
      
      const amount = parseFloat(transaction.amount || 0);
      if (transaction.transaction_type === 'income') {
        totalsByCurrency[currency].income += amount;
      } else {
        totalsByCurrency[currency].expense += amount;
      }
    });

    // Para birimlerine göre kar hesapla
    Object.keys(totalsByCurrency).forEach(currency => {
      totalsByCurrency[currency].profit = 
        totalsByCurrency[currency].income - totalsByCurrency[currency].expense;
    });

    return {
      grouped: sortedGroups,
      total: {
        income: totalIncome,
        expense: totalExpense,
        profit: totalIncome - totalExpense
      },
      totalsByCurrency: Object.values(totalsByCurrency)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const analysis = getFilteredAnalysis();

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 min-h-full">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('accountTracking.title', 'Cari Takip')}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-sm sm:text-lg">{t('accountTracking.subtitle', 'Hesap yönetimi ve para birimi takibi')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setEditingAccount(null);
                resetAccountForm();
                setShowAccountModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm sm:text-base"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>{t('accountTracking.newAccount', 'Yeni Hesap')}</span>
            </button>
            <button
              onClick={() => {
                setEditingTransaction(null);
                resetTransactionForm();
                setShowTransactionModal(true);
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm sm:text-base"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>{t('accountTracking.addTransaction', 'İşlem Ekle')}</span>
            </button>
            <button
              onClick={openConvertModal}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm sm:text-base"
            >
              <ArrowRightLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>{t('accountTracking.convertCurrency', 'Para Çevir')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Hesaplar Listesi */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {accounts.map((account) => {
          const accountTransactions = getAccountTransactions(account.id);
          const income = accountTransactions
            .filter(t => t.transaction_type === 'income')
            .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
          const expense = accountTransactions
            .filter(t => t.transaction_type === 'expense')
            .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

          return (
            <div
              key={account.id}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border-2 p-4 sm:p-6 ${
                account.is_active ? 'border-blue-200 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Wallet className={`h-5 w-5 ${account.is_active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">{account.name}</h3>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  account.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300'
                }`}>
                  {account.is_active ? t('common.active', 'Aktif') : t('common.passive', 'Pasif')}
                </span>
              </div>

              <div className="space-y-2">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('accountTracking.balance', 'Bakiye')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {getCurrencySymbol(account.currency)} {parseFloat(account.balance || 0).toLocaleString('tr-TR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('accountTracking.income', 'Giriş')}</p>
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">
                      {getCurrencySymbol(account.currency)} {income.toLocaleString('tr-TR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('accountTracking.expense', 'Çıkış')}</p>
                    <p className="text-sm font-medium text-red-600 dark:text-red-400">
                      {getCurrencySymbol(account.currency)} {expense.toLocaleString('tr-TR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-2 mt-4">
                <button
                  onClick={() => openEditAccount(account)}
                  className="flex-1 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                >
                  <Edit className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                  {t('common.edit', 'Düzenle')}
                </button>
                <button
                  onClick={() => handleDeleteAccount(account.id)}
                  className="flex-1 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                  {t('common.delete', 'Sil')}
                </button>
              </div>
            </div>
          );
        })}

        {accounts.length === 0 && (
          <div className="col-span-full text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <Wallet className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{t('accountTracking.noAccounts', 'Henüz hesap yok')}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('accountTracking.noAccountsDesc', 'Yeni hesap ekleyerek başlayın.')}</p>
          </div>
        )}
      </div>

      {/* İşlemler Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">{t('accountTracking.transactions', 'İşlemler')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('common.date', 'Tarih')}
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('accountTracking.account', 'Hesap')}
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('common.type', 'Tip')}
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('common.amount', 'Tutar')}
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                  {t('common.description', 'Açıklama')}
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('common.actions', 'İşlemler')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {(() => {
                const startIndex = (transactionsPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedTransactions = transactions.slice(startIndex, endIndex);
                
                if (paginatedTransactions.length === 0) {
                  return (
                    <tr>
                      <td colSpan="6" className="text-center py-12">
                        <DollarSign className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{t('accountTracking.noTransactions', 'Henüz işlem yok')}</h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('accountTracking.noTransactionsDesc', 'İşlem ekleyerek başlayın.')}</p>
                      </td>
                    </tr>
                  );
                }
                
                return paginatedTransactions.map((transaction) => {
                  // Backend'den gelen account_name alanını kullan, yoksa accounts state'inden ara
                  const accountName = transaction.account_name ||
                    accounts.find(a => String(a.id) === String(transaction.account_id))?.name ||
                    t('accountTracking.unknown', 'Bilinmeyen');
                  return (
                    <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                        {new Date(transaction.transaction_date).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                        {accountName}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.transaction_type === 'income'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        }`}>
                          {transaction.transaction_type === 'income' ? (
                            <>
                              <TrendingUp className="h-3 w-3 mr-1" />
                              {t('accountTracking.income', 'Giriş')}
                            </>
                          ) : (
                            <>
                              <TrendingDown className="h-3 w-3 mr-1" />
                              {t('accountTracking.expense', 'Çıkış')}
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                        {getCurrencySymbol(transaction.currency)} {parseFloat(transaction.amount || 0).toLocaleString('tr-TR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell">
                        {transaction.description || '-'}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditTransaction(transaction)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTransaction(transaction.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
          
          {/* İşlemler Sayfalandırma */}
          {transactions.length > itemsPerPage && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {t('common.showing', 'Gösteriliyor')} {((transactionsPage - 1) * itemsPerPage) + 1}-{Math.min(transactionsPage * itemsPerPage, transactions.length)} / {transactions.length}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setTransactionsPage(prev => Math.max(1, prev - 1))}
                  disabled={transactionsPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  {t('common.previous', 'Önceki')}
                </button>
                <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                  {t('common.page', 'Sayfa')} {transactionsPage} / {Math.ceil(transactions.length / itemsPerPage)}
                </span>
                <button
                  onClick={() => setTransactionsPage(prev => Math.min(Math.ceil(transactions.length / itemsPerPage), prev + 1))}
                  disabled={transactionsPage >= Math.ceil(transactions.length / itemsPerPage)}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  {t('common.next', 'Sonraki')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Analiz Bölümü - Filtreli */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">📊 {t('accountTracking.analysis', 'Analiz')}</h2>
        
        {/* Filtreler */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          {/* Hesap Filtresi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('accountTracking.account', 'Hesap')}</label>
            <select
              value={analysisFilters.account_id}
              onChange={(e) => setAnalysisFilters({ ...analysisFilters, account_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="all">{t('accountTracking.allAccounts', 'Tüm Hesaplar')}</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name} ({getCurrencySymbol(account.currency)})
                </option>
              ))}
            </select>
          </div>

          {/* Gelir/Gider Filtresi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('common.type', 'Tür')}</label>
            <select
              value={analysisFilters.transaction_type}
              onChange={(e) => setAnalysisFilters({ ...analysisFilters, transaction_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="all">{t('common.all', 'Tümü')}</option>
              <option value="income">{t('accountTracking.onlyIncome', 'Sadece Gelir')}</option>
              <option value="expense">{t('accountTracking.onlyExpense', 'Sadece Gider')}</option>
            </select>
          </div>

          {/* Zaman Bazı */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('accountTracking.timeBasis', 'Zaman Bazı')}</label>
            <select
              value={analysisFilters.period}
              onChange={(e) => setAnalysisFilters({ ...analysisFilters, period: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="monthly">{t('common.monthly', 'Aylık')}</option>
              <option value="yearly">{t('common.yearly', 'Yıllık')}</option>
            </select>
          </div>

          {/* Başlangıç Tarihi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('common.startDate', 'Başlangıç Tarihi')}</label>
            <input
              type="date"
              value={analysisFilters.start_date}
              onChange={(e) => setAnalysisFilters({ ...analysisFilters, start_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>

          {/* Bitiş Tarihi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('common.endDate', 'Bitiş Tarihi')}</label>
            <input
              type="date"
              value={analysisFilters.end_date}
              onChange={(e) => setAnalysisFilters({ ...analysisFilters, end_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Detaylı Liste */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            {analysisFilters.period === 'monthly' ? t('common.monthly', 'Aylık') : t('common.yearly', 'Yıllık')} {t('accountTracking.details', 'Detaylar')}
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    {analysisFilters.period === 'monthly' ? t('accountTracking.month', 'Ay') : t('accountTracking.year', 'Yıl')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('common.currency', 'Para Birimi')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('accountTracking.income', 'Gelir')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('accountTracking.expense', 'Gider')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('accountTracking.profitLoss', 'Kar/Zarar')}</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">{t('accountTracking.transactionCount', 'İşlem Sayısı')}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {(() => {
                  if (analysis.grouped.length === 0) {
                    return (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          {t('accountTracking.noDataForFilters', 'Seçilen filtreler için veri bulunamadı.')}
                        </td>
                      </tr>
                    );
                  }
                  
                  const startIndex = (analysisPage - 1) * itemsPerPage;
                  const endIndex = startIndex + itemsPerPage;
                  const paginatedGroups = analysis.grouped.slice(startIndex, endIndex);
                  
                  return paginatedGroups.map((group) => {
                    const currencySymbol = getCurrencySymbol(group.currency);
                    const formatOptions = group.currency === 'GOLD' 
                      ? { minimumFractionDigits: 6, maximumFractionDigits: 6 }
                      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
                    
                    return (
                      <tr key={group.key} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {group.periodName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {currencySymbol} ({group.currency})
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600 dark:text-green-400 font-medium">
                          {group.income.toLocaleString('tr-TR', formatOptions)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600 dark:text-red-400 font-medium">
                          {group.expense.toLocaleString('tr-TR', formatOptions)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-medium ${
                          group.profit >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'
                        }`}>
                          {group.profit.toLocaleString('tr-TR', formatOptions)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300">
                          {group.transactions.length}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
            
            {/* Analiz Sayfalandırma */}
            {analysis.grouped.length > itemsPerPage && (
              <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {t('common.showing', 'Gösteriliyor')} {((analysisPage - 1) * itemsPerPage) + 1}-{Math.min(analysisPage * itemsPerPage, analysis.grouped.length)} / {analysis.grouped.length}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setAnalysisPage(prev => Math.max(1, prev - 1))}
                    disabled={analysisPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                  >
                    {t('common.previous', 'Önceki')}
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                    {t('common.page', 'Sayfa')} {analysisPage} / {Math.ceil(analysis.grouped.length / itemsPerPage)}
                  </span>
                  <button
                    onClick={() => setAnalysisPage(prev => Math.min(Math.ceil(analysis.grouped.length / itemsPerPage), prev + 1))}
                    disabled={analysisPage >= Math.ceil(analysis.grouped.length / itemsPerPage)}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                  >
                    {t('common.next', 'Sonraki')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hesap Ekleme/Düzenleme Modal */}
      {showAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingAccount ? t('accountTracking.editAccount', 'Hesap Düzenle') : t('accountTracking.newAccount', 'Yeni Hesap')}
                </h2>
                <button
                  onClick={() => {
                    setShowAccountModal(false);
                    setEditingAccount(null);
                    resetAccountForm();
                  }}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('accountTracking.accountName', 'Hesap Adı')} *
                </label>
                <input
                  type="text"
                  value={accountForm.name}
                  onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={t('accountTracking.accountNamePlaceholder', 'Örn: Nakit Kasa, Banka Hesabı')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.currency', 'Para Birimi')} *
                </label>
                <select
                  value={accountForm.currency}
                  onChange={(e) => setAccountForm({ ...accountForm, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="TRY">{t('currency.try', '₺ Türk Lirası')}</option>
                  <option value="USD">{t('currency.usd', '$ Dolar')}</option>
                  <option value="EUR">{t('currency.eur', '€ Euro')}</option>
                  <option value="GBP">{t('currency.gbp', '£ Sterlin')}</option>
                  <option value="GOLD">{t('currency.gold', 'Altın')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('accountTracking.initialBalance', 'Başlangıç Bakiyesi')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={accountForm.balance}
                  onChange={(e) => setAccountForm({ ...accountForm, balance: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0.00"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={accountForm.is_active}
                  onChange={(e) => setAccountForm({ ...accountForm, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                />
                <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  {t('common.active', 'Aktif')}
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex space-x-3">
              <button
                onClick={editingAccount ? handleUpdateAccount : handleCreateAccount}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                {editingAccount ? t('common.update', 'Güncelle') : t('accountTracking.create', 'Oluştur')}
              </button>
              <button
                onClick={() => {
                  setShowAccountModal(false);
                  setEditingAccount(null);
                  resetAccountForm();
                }}
                className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg font-medium"
              >
                {t('common.cancel', 'İptal')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* İşlem Ekleme/Düzenleme Modal */}
      {showTransactionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingTransaction ? t('accountTracking.editTransaction', 'İşlem Düzenle') : t('accountTracking.newTransaction', 'Yeni İşlem')}
                </h2>
                <button
                  onClick={() => {
                    setShowTransactionModal(false);
                    setEditingTransaction(null);
                    resetTransactionForm();
                  }}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('accountTracking.account', 'Hesap')} *
                </label>
                <select
                  value={transactionForm.account_id}
                  onChange={(e) => {
                    // UUID string karşılaştırması (parseInt kullanma!)
                    const account = accounts.find(a => String(a.id) === String(e.target.value));
                    setTransactionForm({
                      ...transactionForm,
                      account_id: e.target.value,
                      currency: account?.currency || 'TRY'
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('accountTracking.selectAccount', 'Hesap Seçin')}</option>
                  {accounts.filter(a => a.is_active).map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({getCurrencySymbol(account.currency)})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('accountTracking.transactionType', 'İşlem Tipi')} *
                </label>
                <select
                  value={transactionForm.transaction_type}
                  onChange={(e) => setTransactionForm({ ...transactionForm, transaction_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="income">{t('accountTracking.incomeType', 'Giriş (Gelir)')}</option>
                  <option value="expense">{t('accountTracking.expenseType', 'Çıkış (Gider)')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.amount', 'Tutar')} *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={transactionForm.amount}
                  onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.currency', 'Para Birimi')} *
                </label>
                <select
                  value={transactionForm.currency}
                  onChange={(e) => setTransactionForm({ ...transactionForm, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="TRY">{t('currency.try', '₺ Türk Lirası')}</option>
                  <option value="USD">{t('currency.usd', '$ Dolar')}</option>
                  <option value="EUR">{t('currency.eur', '€ Euro')}</option>
                  <option value="GBP">{t('currency.gbp', '£ Sterlin')}</option>
                  <option value="GOLD">{t('currency.gold', 'Altın')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.date', 'Tarih')} *
                </label>
                <input
                  type="date"
                  value={transactionForm.transaction_date}
                  onChange={(e) => setTransactionForm({ ...transactionForm, transaction_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('common.description', 'Açıklama')}
                </label>
                <textarea
                  value={transactionForm.description}
                  onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="3"
                  placeholder={t('accountTracking.descriptionPlaceholder', 'İşlem açıklaması...')}
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex space-x-3">
              <button
                onClick={editingTransaction ? handleUpdateTransaction : handleCreateTransaction}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                {editingTransaction ? t('common.update', 'Güncelle') : t('common.add', 'Ekle')}
              </button>
              <button
                onClick={() => {
                  setShowTransactionModal(false);
                  setEditingTransaction(null);
                  resetTransactionForm();
                }}
                className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg font-medium"
              >
                {t('common.cancel', 'İptal')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Para Birimi Çevirme Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('accountTracking.convertCurrency', 'Para Birimi Çevir')}</h2>
                <button
                  onClick={() => {
                    setShowConvertModal(false);
                    resetConvertForm();
                  }}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('accountTracking.sourceAccount', 'Kaynak Hesap')} *
                </label>
                <select
                  value={convertForm.from_account_id}
                  onChange={(e) => setConvertForm({ ...convertForm, from_account_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('accountTracking.selectAccount', 'Hesap Seçin')}</option>
                  {accounts.filter(a => a.is_active).map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({getCurrencySymbol(account.currency)}) - {t('accountTracking.balance', 'Bakiye')}: {getCurrencySymbol(account.currency)} {parseFloat(account.balance || 0).toLocaleString('tr-TR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('accountTracking.targetAccount', 'Hedef Hesap')} *
                </label>
                <select
                  value={convertForm.to_account_id}
                  onChange={(e) => setConvertForm({ ...convertForm, to_account_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{t('accountTracking.selectAccount', 'Hesap Seçin')}</option>
                  {accounts
                    .filter(a => a.is_active && String(a.id) !== String(convertForm.from_account_id))
                    .map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name} ({getCurrencySymbol(account.currency)})
                      </option>
                    ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {(() => {
                      // UUID string karşılaştırması (parseInt kullanma!)
                      const fromAccount = accounts.find(a => String(a.id) === String(convertForm.from_account_id));
                      return fromAccount ? `${t('accountTracking.sourceAmount', 'Kaynak Tutar')} (${getCurrencySymbol(fromAccount.currency)}) *` : `${t('accountTracking.sourceAmount', 'Kaynak Tutar')} *`;
                    })()}
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={convertForm.amount}
                    onChange={(e) => {
                      const newAmount = e.target.value;
                      setConvertForm(prev => ({ 
                        ...prev, 
                        amount: newAmount,
                        to_amount: '' // Hedef tutarı temizle, useEffect hesaplayacak
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {(() => {
                      // UUID string karşılaştırması (parseInt kullanma!)
                      const toAccount = accounts.find(a => String(a.id) === String(convertForm.to_account_id));
                      return toAccount ? `${t('accountTracking.targetAmount', 'Hedef Tutar')} (${getCurrencySymbol(toAccount.currency)}) *` : `${t('accountTracking.targetAmount', 'Hedef Tutar')} *`;
                    })()}
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={convertForm.to_amount}
                    onChange={(e) => {
                      const newToAmount = e.target.value;
                      setConvertForm(prev => ({ 
                        ...prev, 
                        to_amount: newToAmount,
                        amount: '' // Kaynak tutarı temizle, useEffect hesaplayacak
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('accountTracking.enterOneAmount', 'Kaynak veya hedef tutardan birini girin, diğeri otomatik hesaplanacaktır.')}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('accountTracking.exchangeRate', 'Çevirme Oranı (Otomatik)')}
                </label>
                <input
                  type="number"
                  step="0.000000000001"
                  value={convertForm.exchange_rate}
                  onChange={(e) => setConvertForm({ ...convertForm, exchange_rate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="1.000000000000"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {convertForm.from_account_id && convertForm.to_account_id && convertForm.amount && (
                    (() => {
                      // UUID string karşılaştırması (parseInt kullanma!)
                      const fromAccount = accounts.find(a => String(a.id) === String(convertForm.from_account_id));
                      // UUID string karşılaştırması (parseInt kullanma!)
                      const toAccount = accounts.find(a => String(a.id) === String(convertForm.to_account_id));
                      if (fromAccount && toAccount) {
                        const fromAmount = parseFloat(convertForm.amount) || 0;
                        const rate = parseFloat(convertForm.exchange_rate) || 1;
                        const toAmount = fromAmount * rate;
                        
                        // Çevirme oranı için maksimum 12 ondalık hane göster
                        let rateDisplay = rate.toFixed(12);
                        let toAmountDisplay = toAmount.toFixed(4);
                        let fromAmountDisplay = fromAmount.toFixed(2);
                        
                        // Altın için daha okunabilir gösterim
                        if (toAccount.currency === 'GOLD' && toAmount < 1) {
                          toAmountDisplay = toAmount.toFixed(6);
                          rateDisplay = rate.toFixed(12);
                        } else if (fromAccount.currency === 'GOLD' && fromAmount < 1) {
                          fromAmountDisplay = fromAmount.toFixed(6);
                          rateDisplay = rate.toFixed(12);
                        }
                        
                        return `1 ${fromAccount.currency} = ${rateDisplay} ${toAccount.currency} (${fromAmountDisplay} ${fromAccount.currency} = ${toAmountDisplay} ${toAccount.currency})`;
                      }
                      return '';
                    })()
                  )}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex space-x-3">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleConvertCurrency();
                }}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                {t('accountTracking.convert', 'Çevir')}
              </button>
              <button
                onClick={() => {
                  setShowConvertModal(false);
                  resetConvertForm();
                }}
                className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg font-medium"
              >
                {t('common.cancel', 'İptal')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountTracking;
